import { analytics } from '~/services/analytics'
import { useRoute } from 'vue-router'
import { $istDevEnv } from '~/utils/helper'
import { useUserStore } from '~/stores/useUserStore'

/**
 * Centralized wrapper around `posthog-js` for product analytics.
 *
 * Goals:
 *  - One place to define the base event context (customer, user, plan, page).
 *  - No-ops in dev and outside the browser so analytics never break the app.
 *  - Never throws: analytics failures are swallowed silently.
 *
 * Event naming follows `object_action` (lowercase + underscores), e.g.
 * `agent_updated`, `call_audio_played`, `crm_file_uploaded`.
 *
 * Do not send PII: no email, phone numbers, caller numbers, transcripts,
 * summaries, captured information, or notes. Internal UUIDs only.
 * Exception: `login_attempted` / `login_failed` include the submitted email
 * so failed sign-in can be queried before identify() runs.
 */
export const usePostHog = () => {
  const capture = (event: string, properties?: Record<string, unknown>) => {
    if ($istDevEnv()) return

    try {
      const userStore = useUserStore()
      const base: Record<string, unknown> = {
        environment: import.meta.env.MODE,
        customer_id: userStore.customerId || undefined,
        user_id: userStore.userId || undefined,
        role_name: userStore.roleName || undefined,
        agent_count: Array.isArray(userStore.agents) ? userStore.agents.length : undefined,
      }

      let sourcePage: string | undefined
      try {
        const route = useRoute()
        sourcePage = route?.name ? String(route.name) : undefined
      }
      catch {
        sourcePage = undefined
      }
      if (sourcePage) base.source_page = sourcePage

      analytics.capture(event, { ...base, ...(properties || {}) })
    }
    catch {
      // Analytics must never break the app.
    }
  }

  const captureException = (error: unknown, extra?: Record<string, unknown>) => {
    if ($istDevEnv()) return
    try {
      analytics.captureException(error, extra)
    }
    catch {
      // ignored
    }
  }

  return { capture, captureException }
}
